import React, {useState, useEffect} from "react";
import {Box, TextField, Button, CircularProgress, Alert, Snackbar} from "@mui/material";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import SmallHeading from "../../components/SmallHeading";
import ColumnBox from "../../components/ColumnBox";
import RowBox from "../../components/RowBox";
import {apiHelper, baseUrl} from "helpers/util";

const AOMCRecordDisplay = ({fetchedRecord, onMarkAttendance, loading}) => {
    const getCurrentISTTime = () => {
        const now = new Date();
        return new Date(now.toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
    };

    const getDateStr = () => {
        return getCurrentISTTime().toLocaleString("en-IN", {
            month: "long",
            day: "2-digit",
            year: "numeric"
        });
    };

    const getTimeStr = () => {
        return getCurrentISTTime().toLocaleString("en-IN", {
            hour12: true,
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    if (!fetchedRecord) return null;

    return (
        <Box
            sx={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "15px",
                marginTop: "20px",
                backgroundColor: "#f9f9f9",
            }}
        >
            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>Name:</span>
                {fetchedRecord["Name"] || "N/A"}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>Mobile No:</span>
                {fetchedRecord["Mobile No."] || "N/A"}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>Location:</span>
                {fetchedRecord["Area of Stay"] || "N/A"}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>FOLK Level:</span>
                {fetchedRecord["FOLK Level (Display)"] || "N/A"}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>Occupation:</span>
                {fetchedRecord["Occupation"] || "N/A"}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>FOLK Guide:</span>
                {fetchedRecord["FOLK Guide"] || "N/A"}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>Date:</span> {getDateStr()}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>Time:</span> {getTimeStr()}
            </div>

            <div style={{marginBottom: "4mm"}}>
                <span style={{fontWeight: "500", marginRight: "4px"}}>Photo:</span>
                <img
                    src={fetchedRecord["Photo"] && fetchedRecord["Photo"][0]?.thumbnails?.large?.url}
                    alt="User"
                    style={{maxWidth: "100%", maxHeight: "100%", objectFit: "contain"}}
                />
            </div>

            <div style={{display: "flex", justifyContent: "center", marginTop: "15px"}}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onMarkAttendance}
                    disabled={loading}
                    sx={{minWidth: "150px"}}
                >
                    {loading ? <CircularProgress size="1.5rem" color="inherit" /> : "Mark AOMC Attendance"}
                </Button>
            </div>
        </Box>
    );
};

export default function AOMCAttendance() {
    const [mobileNo, setMobileNo] = useState("");
    const [fetchedRecord, setFetchedRecord] = useState(null);
    const [loading, setLoading] = useState(false);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorColor, setErrorColor] = useState("error");

    const resetForm = () => {
        setMobileNo("");
        setFetchedRecord(null);
        setErrorMessage(null);
        setErrorColor("error");
        setLoading(false);
        setAttendanceLoading(false);
    };

    const showSuccessMessage = () => {
        setErrorColor("success");
        setErrorMessage("🙏 AOMC Attendance marked successfully! Hare Krishna! 🌸\\nMay Lord Krishna bless you with spiritual progress.");
        
        // Reset form after 3 seconds
        setTimeout(() => {
            resetForm();
        }, 3000);
    };

    // Auto-search when mobile number changes (only on mobile devices)
    useEffect(() => {
        // Check if device is mobile
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
        
        if (isMobile && mobileNo.length >= 3) {
            const timeoutId = setTimeout(() => {
                searchByMobileNo(mobileNo);
            }, 500);
            return () => clearTimeout(timeoutId);
        } else if (!isMobile && mobileNo.length >= 10) {
            // For desktop, search only when full mobile number is entered
            const timeoutId = setTimeout(() => {
                searchByMobileNo(mobileNo);
            }, 500);
            return () => clearTimeout(timeoutId);
        } else {
            setFetchedRecord(null);
        }
    }, [mobileNo]);

    const searchByMobileNo = async (mobile) => {
        if (!mobile || mobile.length < 3) return;

        setLoading(true);
        setErrorMessage(null);

        try {
            console.log("AOMC API Request Details:", {
                url: `${baseUrl}/api/aomc_attendance?mobileNo=${encodeURIComponent(mobile)}`,
                mobile: mobile,
                timestamp: new Date().toISOString()
            });

            const response = await apiHelper.get({
                url: `${baseUrl}/api/aomc_attendance?mobileNo=${encodeURIComponent(mobile)}`,
            });

            console.log("AOMC API Response Details:", {
                status: response.status,
                json: response.json,
                hasRecords: response.json && Array.isArray(response.json),
                recordCount: response.json ? response.json.length : 0,
                timestamp: new Date().toISOString()
            });

            if (response.status && response.json) {
                if (response.json && response.json.length > 0) {
                    setFetchedRecord(response.json[0]);
                    setErrorColor("success");
                    setErrorMessage("AOMC record found successfully!");
                } else {
                    setFetchedRecord(null);
                    setErrorColor("warning");
                    setErrorMessage("No AOMC record found for this mobile number.");
                }
            } else {
                setFetchedRecord(null);
                setErrorColor("error");
                setErrorMessage("Failed to search AOMC records. Please try again.");
            }
        } catch (error) {
            console.error("AOMC search error details:", {
                message: error.message,
                stack: error.stack,
                url: `${baseUrl}/api/aomc_attendance?mobileNo=${encodeURIComponent(mobile)}`,
                mobile: mobile,
                timestamp: new Date().toISOString()
            });
            setFetchedRecord(null);
            setErrorColor("error");
            setErrorMessage(`AOMC search failed: ${error.message || 'Unknown error occurred'}. Please check console for details.`);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAttendance = async () => {
        if (!fetchedRecord) return;

        setAttendanceLoading(true);
        setErrorMessage(null);

        try {
            const response = await apiHelper.post({
                url: `${baseUrl}/api/aomc_attendance`,
                data: {
                    recordId: fetchedRecord["Record ID"],
                    mobileNo: fetchedRecord["Mobile No."],
                    name: fetchedRecord["Name"],
                },
            });

            if (response.status && response.json) {
                showSuccessMessage();
            } else {
                setErrorColor("error");
                setErrorMessage("Failed to mark AOMC attendance. Please try again.");
            }
        } catch (error) {
            console.error("AOMC attendance marking error details:", {
                message: error.message,
                stack: error.stack,
                url: `${baseUrl}/api/aomc_attendance`,
                payload: {
                    recordId: fetchedRecord["Record ID"],
                    mobileNo: fetchedRecord["Mobile No."],
                    name: fetchedRecord["Name"]
                },
                timestamp: new Date().toISOString()
            });
            setErrorColor("error");
            setErrorMessage(`AOMC attendance marking failed: ${error.message || 'Unknown error occurred'}. Please check console for details.`);
        } finally {
            setAttendanceLoading(false);
        }
    };

    return (
        <Box sx={{display: "flex", flexDirection: "row", justifyContent: "center", padding: "15px"}}>
            <ColumnBox>
                <SmallHeading sx={{alignSelf: "center"}}>AOMC Attendance</SmallHeading>
                <ColumnBox sx={{alignItems: "center", maxWidth: "500px", width: "100%"}}>
                    <RowBox
                        sx={{
                            width: "100%",
                            marginTop: "15px",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{width: "85%", display: "flex", alignItems: "center"}}>
                            <TextField
                                required
                                label="Mobile No."
                                autoFocus
                                size="small"
                                value={mobileNo}
                                onChange={(e) => setMobileNo(e.target.value)}
                                sx={{flexGrow: 1, marginLeft: "30px"}}
                                placeholder="Enter mobile number to search..."
                            />

                            <Box sx={{marginLeft: "10px"}}>
                                {loading ? <CircularProgress size="1.5rem" /> : <MobileFriendlyIcon size="1.5rem" />}
                            </Box>
                        </Box>
                    </RowBox>

                    {errorMessage && (
                        <Snackbar
                            open={!!errorMessage}
                            autoHideDuration={errorColor === "success" ? 3000 : 4000}
                            onClose={() => setErrorMessage(null)}
                            anchorOrigin={{vertical: "top", horizontal: "center"}}
                        >
                            <Alert 
                                severity={errorColor} 
                                onClose={() => setErrorMessage(null)} 
                                sx={{
                                    width: "100%",
                                    fontSize: "1.1rem",
                                    fontWeight: errorColor === "success" ? "bold" : "normal",
                                    backgroundColor: errorColor === "success" ? "#e8f5e8" : undefined,
                                    color: errorColor === "success" ? "#2e7d32" : undefined,
                                    whiteSpace: "pre-line"
                                }}
                            >
                                {errorMessage}
                            </Alert>
                        </Snackbar>
                    )}

                    <AOMCRecordDisplay
                        fetchedRecord={fetchedRecord}
                        onMarkAttendance={handleMarkAttendance}
                        loading={attendanceLoading}
                    />
                </ColumnBox>
            </ColumnBox>
        </Box>
    );
}